// src/context/FocusModeContext.tsx
import React, { createContext, useContext, useState, useCallback } from 'react';

export type FocusMode = 'ship' | 'triage' | 'monitor' | 'demo';

interface FocusModeContextType {
  mode: FocusMode;
  setMode: (mode: FocusMode) => void;
  isPanelFocused: (panelId: string) => boolean;
}

const FocusModeContext = createContext<FocusModeContextType | undefined>(undefined);

// Which panels are relevant in each focus mode
const focusMap: Record<FocusMode, string[]> = {
  ship: ['focus-panel', 'urgent-panel', 'active-sessions'],
  triage: ['inbox-panel', 'issues-panel', 'urgent-panel'],
  monitor: [], // all panels are equal
  demo: [], // demo handles its own visibility
};

export const FocusModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<FocusMode>('monitor');

  const setMode = useCallback((newMode: FocusMode) => {
    setModeState(newMode);
    // Auto-reset to monitor after 30 mins in ship/triage
    if (newMode === 'ship' || newMode === 'triage') {
      setTimeout(() => setModeState('monitor'), 30 * 60 * 1000);
    }
  }, []);

  const isPanelFocused = useCallback((panelId: string) => {
    if (mode === 'monitor' || mode === 'demo') return false;
    return focusMap[mode].includes(panelId);
  }, [mode]);

  return (
    <FocusModeContext.Provider value={{ mode, setMode, isPanelFocused }}>
      <div className={`focus-mode--${mode}`} style={{ minHeight: '100vh' }}>
        {children}
      </div>
    </FocusModeContext.Provider>
  );
};

export const useFocusMode = () => {
  const context = useContext(FocusModeContext);
  if (!context) throw new Error('useFocusMode must be used within FocusModeProvider');
  return context;
};

export default FocusModeContext;
